<template>
  <div class="gc-table-shell" :class="{ 'is-loading': loading }">
    <LoadingOverlay v-if="loading && safeRows.length" compact />

    <div class="gc-table-scroll" :class="{ 'has-max-height': !!maxHeight }" :style="scrollStyle">
      <table class="gc-admin-table" :style="tableStyle">
        <colgroup>
          <col
            v-for="column in safeColumns"
            :key="column.key"
            :style="columnStyle(column)"
          >
        </colgroup>

        <thead>
          <tr>
            <th
              v-for="column in safeColumns"
              :key="column.key"
              :class="headerClass(column)"
              @click="toggleSort(column)"
            >
              <span class="gc-th-inner">
                {{ column.label }}
                <span v-if="isSortable(column)" class="gc-sort-ind" :class="{ active: sortKey === column.key }">{{ sortGlyph(column) }}</span>
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(row, index) in displayRows"
            :key="resolveRowKey(row, index)"
            :class="tableRowClass(row, index)"
            @click="handleRowClick(row, index)"
          >
            <td
              v-for="column in safeColumns"
              :key="column.key"
              :class="cellClass(column)"
            >
              <slot
                :name="'cell-' + column.key"
                :row="row"
                :value="cellValue(row, column)"
                :column="column"
                :index="index"
              >
                <!-- Columns built at runtime (no named slot to target) render here. -->
                <slot name="cell" :row="row" :value="cellValue(row, column)" :column="column" :index="index">
                  <span class="gc-cell-text">{{ formatValue(cellValue(row, column)) }}</span>
                </slot>
              </slot>
            </td>
          </tr>

          <tr v-if="!safeRows.length">
            <td :colspan="safeColumns.length || 1" class="gc-table-empty">
              <slot name="empty">
                <LoadingOverlay v-if="loading" mode="block" compact clear min-height="220px" />
                <span v-else>{{ emptyText }}</span>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import LoadingOverlay from '../../widgets/LoadingOverlay.vue'

export default {
  name: 'AdminTable',
  components: {
    LoadingOverlay
  },
  props: {
    columns: {
      type: Array,
      default: function () {
        return []
      }
    },
    rows: {
      type: Array,
      default: function () {
        return []
      }
    },
    rowKey: {
      type: [String, Function],
      default: 'id'
    },
    rowClass: {
      type: [String, Function],
      default: ''
    },
    emptyText: {
      type: String,
      default: 'No records found.'
    },
    loading: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    },
    maxHeight: {
      type: [String, Number],
      default: ''
    },
    minWidth: {
      type: [String, Number],
      default: '980px'
    },
    // Opt-in client-side sorting. Mark columns sortable with `sortable: true`
    // (and optionally `sortAccessor: row => comparable` / `defaultDir`), and set
    // an initial sort here, e.g. defaultSort = { key: 'cost', dir: 'desc' }.
    // When nothing is configured, rows render in their given order — so this is
    // purely presentational and never reorders product/compatibility data.
    defaultSort: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      sortKey: this.defaultSort && this.defaultSort.key ? this.defaultSort.key : null,
      sortDir: this.defaultSort && this.defaultSort.dir ? this.defaultSort.dir : 'desc'
    }
  },
  computed: {
    // Props default only when the binding is `undefined`; a parent that passes
    // `null` (e.g. an unloaded list) would otherwise crash render on `.length`.
    safeColumns () {
      return Array.isArray(this.columns) ? this.columns : []
    },
    safeRows () {
      return Array.isArray(this.rows) ? this.rows : []
    },
    // Rows in display order. Returns the original array untouched unless an
    // active, sortable column is selected — then a stable-sorted copy (never
    // mutates the prop, preserves original order for equal keys).
    displayRows () {
      const rows = this.safeRows
      if (!this.sortKey) return rows
      const col = this.safeColumns.find((c) => c.key === this.sortKey)
      if (!col || !col.sortable) return rows
      const dir = this.sortDir === 'asc' ? 1 : -1
      return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
          const c = this.compareValues(this.sortValueFor(a.row, col), this.sortValueFor(b.row, col))
          return c !== 0 ? c * dir : a.index - b.index
        })
        .map((entry) => entry.row)
    },
    scrollStyle () {
      if (!this.maxHeight) return null
      const value = typeof this.maxHeight === 'number' ? this.maxHeight + 'px' : this.maxHeight
      return { maxHeight: value }
    },
    tableStyle () {
      const value = typeof this.minWidth === 'number' ? this.minWidth + 'px' : this.minWidth
      return value ? { minWidth: value } : null
    }
  },
  methods: {
    resolveRowKey (row, index) {
      if (typeof this.rowKey === 'function') {
        const key = this.rowKey(row, index)
        return key === undefined || key === null ? index : key
      }
      return row && row[this.rowKey] !== undefined && row[this.rowKey] !== null
        ? row[this.rowKey]
        : index
    },
    tableRowClass (row, index) {
      const classes = ['gc-table-row']
      if (this.clickable) classes.push('is-clickable')
      if (typeof this.rowClass === 'function') {
        const cls = this.rowClass(row, index)
        if (cls) classes.push(cls)
      } else if (this.rowClass) {
        classes.push(this.rowClass)
      }
      return classes
    },
    handleRowClick (row, index) {
      if (!this.clickable) return
      this.$emit('row-click', row, index)
    },
    cellValue (row, column) {
      if (!row || !column) return ''
      if (typeof column.value === 'function') {
        return column.value(row)
      }
      return row[column.key]
    },
    isSortable (column) {
      return !!(column && column.sortable)
    },
    sortValueFor (row, column) {
      if (column && typeof column.sortAccessor === 'function') {
        return column.sortAccessor(row)
      }
      return this.cellValue(row, column)
    },
    toggleSort (column) {
      if (!this.isSortable(column)) return
      if (this.sortKey === column.key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = column.key
        this.sortDir = column.defaultDir || 'desc'
      }
      this.$emit('sort', { key: this.sortKey, dir: this.sortDir })
    },
    sortGlyph (column) {
      if (this.sortKey !== column.key) return '⇅' // ⇅ idle
      return this.sortDir === 'asc' ? '▲' : '▼' // ▲ / ▼
    },
    // Type-aware comparison: numbers (or numeric strings) numerically, dates by
    // time, everything else as a locale string. Empty/null sort last.
    compareValues (a, b) {
      const ae = a === undefined || a === null || a === ''
      const be = b === undefined || b === null || b === ''
      if (ae && be) return 0
      if (ae) return 1
      if (be) return -1
      if (typeof a === 'number' && typeof b === 'number') return a - b
      const na = Number(a)
      const nb = Number(b)
      if (!isNaN(na) && !isNaN(nb)) return na - nb
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
    },
    formatValue (value) {
      if (value === undefined || value === null || value === '') return '-'
      return value
    },
    columnStyle (column) {
      if (!column || !column.width) return null
      return { width: column.width }
    },
    headerClass (column) {
      return [
        'gc-table-head',
        column.align ? 'is-' + column.align : '',
        this.isSortable(column) ? 'is-sortable' : '',
        this.isSortable(column) && this.sortKey === column.key ? 'is-sorted' : '',
        column.className || ''
      ]
    },
    cellClass (column) {
      return [
        'gc-table-cell',
        column.align ? 'is-' + column.align : '',
        column.nowrap ? 'is-nowrap' : '',
        column.breakable ? 'is-breakable' : '',
        column.className || ''
      ]
    }
  }
}
</script>

<style scoped>
.gc-table-shell {
  position: relative;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(8, 12, 18, 0.48);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
}

.gc-table-shell.is-loading {
  border-color: rgba(25, 151, 198, 0.45);
}

.gc-table-scroll {
  overflow-x: auto;
  border-radius: 8px;
}

.gc-table-scroll.has-max-height {
  overflow: auto;
}

.gc-admin-table {
  width: 100%;
  min-width: 980px;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
}

.gc-table-head {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
  background: rgba(8, 12, 18, 0.98);
  color: #818b9d;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  line-height: 1.2;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.gc-table-head.is-sortable {
  cursor: pointer;
  user-select: none;
}

.gc-table-head.is-sortable:hover {
  color: #aeb8ca;
}

.gc-table-head.is-sorted {
  color: #1fb2e8;
}

.gc-th-inner {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.gc-table-head.is-right .gc-th-inner {
  flex-direction: row-reverse;
}

.gc-sort-ind {
  font-size: 9px;
  opacity: 0.35;
  transition: opacity 0.15s ease;
}

.gc-sort-ind.active {
  opacity: 1;
  color: #1fb2e8;
}

.gc-table-cell {
  padding: 12px;
  border-bottom: 1px solid rgba(47, 51, 61, 0.78);
  color: #cfd4df;
  line-height: 1.38;
  vertical-align: middle;
}

.gc-table-row:last-child .gc-table-cell {
  border-bottom: 0;
}

.gc-table-row.is-clickable {
  cursor: pointer;
}

.gc-table-row.is-clickable:hover {
  background: rgba(25, 151, 198, 0.065);
}

.gc-table-cell.is-right,
.gc-table-head.is-right {
  text-align: right;
}

.gc-table-cell.is-center,
.gc-table-head.is-center {
  text-align: center;
}

.gc-table-cell.is-nowrap {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gc-table-cell.is-breakable {
  overflow-wrap: anywhere;
}

.gc-cell-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gc-table-empty {
  padding: 24px 18px;
  color: #768093;
  text-align: center;
}

</style>
